import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Divider,
    Alert,
    Tabs,
    Tab,
    Checkbox,
    FormControlLabel,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import { useParams } from "react-router-dom";
import { authHeader } from "../auth";
import { deriveRanking } from "../utils/ranking";
// Même formulaire Raceheader que pour les autres courses FIS (page
// "Course & exports"). ⚠️ Adapter ces deux chemins si les fichiers
// timing-export ne sont pas dans src/pages/timing/.
import RaceHeaderForm from "../components/timingsoftware/RaceHeaderForm";
import { deriveSeason, validateHeader } from "../components/timingsoftware/raceTypes";
import { startlistPdf, resultListPdf, fisFileBase, preloadPdfAssets } from "../utils/parallelFisPdf";
import ParallelSubNav from "./ParallelSubNav";
import { useParallelBranding } from "../utils/useParallelBranding";

// Disciplines proposées pour un parallèle télémark. ⚠️ Codes à confirmer
// avec la FIS : PSL est celui du fichier de référence SUI8512.xml.
const PARALLEL_DISCIPLINES = [
    { value: "PSL", label: "PSL — Parallel Slalom" },
    { value: "PSP", label: "PSP — Parallel Sprint" },
    { value: "PGS", label: "PGS — Parallel Giant Slalom" },
];

const RESULT_TYPES = [
    { value: "official", label: "official — résultats officiels" },
    { value: "unofficial", label: "unofficial — résultats non officiels" },
    { value: "partial", label: "partial — résultats partiels" },
    { value: "startlist", label: "startlist — liste de départ" },
];

const JURY_FUNCTIONS = [
    "HeadJudge",
    "ChiefCompetition",
    "RaceDirector",
    "ContestDirector",
    "TechnicalDelegate",
    "StartReferee",
    "FinishReferee",
    "ChiefOfFinish",
    "Referee",
];

const DEFAULT_RACEINFO = {
    usedfislist: "",
    appliedpenalty: "",
    calculatedpenalty: "",
    timingby: "",
    dataprocessingby: "",
    softwarecompany: "",
    softwarename: "",
    softwareversion: "",
};

// Infos parcours par manche (<Runinfo no="1..4">), au format lu par
// buildRuninfoXml dans finals.js.
const EMPTY_RUN = () => ({
    course: {
        name: "",
        homologation: "",
        startelev: "",
        finishelev: "",
        length: "",
        gates: "",
        turninggates: "",
        coursesetter: { lastname: "", firstname: "", nation: "" },
        forerunners: [],
    },
    starttime: "",
    weather: { weather: "", snow: "", temperatureair: "" },
});

function normalizeRuns(runs) {
    return [0, 1, 2, 3].map((i) => {
        const r = (runs || [])[i] || {};
        const base = EMPTY_RUN();
        return {
            ...base,
            ...r,
            course: {
                ...base.course,
                ...(r.course || {}),
                coursesetter: { ...base.course.coursesetter, ...(r.course?.coursesetter || {}) },
                forerunners: r.course?.forerunners || [],
            },
            weather: { ...base.weather, ...(r.weather || {}) },
        };
    });
}

// Logos de la "Config impression" des courses parallèles : même source
// que le bracket / tableau des paires (hook useParallelBranding).
const hasLogo = (b) => !!(b && (b.bannerImage || b.logoLeft || b.logoRight || b.footerLogo));

const ROUND_LABEL = { 1: "Finale", 2: "Demi-finales", 4: "Quarts de finale", 8: "1/8 de finale", 16: "1/16 de finale", 32: "1/32 de finale" };

const pad2 = (v) => (v === "" || v == null ? "" : String(v).padStart(2, "0"));

// ---------------------------------------------------------------------
// Conversion entre la config stockée (/api/fis-config, format XML :
// raceheader.codex/nation/category/day/month/year...) et le format du
// formulaire commun RaceHeaderForm (codex/date "JJ.MM.AAAA"/event...).
// Le format stocké ne change PAS : finals.js le lit tel quel.
// ---------------------------------------------------------------------
function toForm(rh = {}, ri = {}) {
    let codex = String(rh.codex || "").trim();
    let nation = String(rh.nation || "").trim().toUpperCase();
    const notes = [];

    // Correction des configs saisies à l'envers avec l'ancien formulaire
    // (ex: Codex "SUI" et Nation "SUI8512.019" = identifiant FIS complet) :
    // on récupère nation = SUI et codex = 8512.
    const fullId = /^([A-Z]{3})(\d{4})/.exec(nation) || /^([A-Z]{3})(\d{4})/.exec(codex.toUpperCase());
    if (fullId && !/^\d{1,4}$/.test(codex)) {
        notes.push(`Codex/Nation corrigés depuis « ${rh.codex} / ${rh.nation} » → codex ${fullId[2]}, nation ${fullId[1]}. Vérifiez puis enregistrez.`);
        nation = fullId[1];
        codex = fullId[2];
    }

    const date = rh.day && rh.month && rh.year ? `${pad2(rh.day)}.${pad2(rh.month)}.${rh.year}` : "";
    const derived = deriveSeason(date);
    return {
        form: {
            raceType: "fis",
            sector: "TM",
            codex,
            date,
            event: rh.eventname || "",
            place: rh.place || "",
            season: rh.season || derived,
            seasonManual: !!rh.season && rh.season !== derived,
            nation: nation || "SUI",
            fisCategory: rh.category || "",
            discipline: rh.discipline || "",
            gender: rh.gender || "",
            timing: ri.timingby || "",
        },
        notes,
    };
}

function fromForm(form, resultType) {
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(form.date || "");
    return {
        gender: form.gender,
        season: form.season || deriveSeason(form.date),
        codex: form.codex,
        nation: form.nation,
        discipline: form.discipline,
        category: form.fisCategory,
        day: m ? m[1] : "",
        month: m ? m[2] : "",
        year: m ? m[3] : "",
        type: resultType,
        eventname: form.event,
        place: form.place,
    };
}

export default function ParallelFisExport() {
    const { category } = useParams();

    const [form, setForm] = useState(() => toForm().form);
    const [resultType, setResultType] = useState("official");
    const [raceinfo, setRaceinfo] = useState(DEFAULT_RACEINFO);
    const [jury, setJury] = useState([]);
    const [notes, setNotes] = useState([]);
    const [runTab, setRunTab] = useState(0);
    const [bracket, setBracket] = useState(null);
    const [docRound, setDocRound] = useState(0);

    // Logo 321-timing du pied de page des PDF : chargé une fois à l'ouverture.
    useEffect(() => {
        preloadPdfAssets();
    }, []);

    // --- Logos du PDF résultats (RL) ---
    const [withLogos, setWithLogos] = useState(true);
    const brandingRaw = useParallelBranding(category);
    const branding = hasLogo(brandingRaw) ? brandingRaw : null;

    // --- Envoi à la FIS ---
    const [sendWhat, setSendWhat] = useState("results"); // "results" | "startlist"
    const [transport, setTransport] = useState("email"); // "email" | "api"
    const [sendTo, setSendTo] = useState("results@fis-ski.com");
    const [subject, setSubject] = useState("");
    const [subjectEdited, setSubjectEdited] = useState(false);
    const [includeXml, setIncludeXml] = useState(true);
    const [testMode, setTestMode] = useState(false);
    const [sending, setSending] = useState(false);
    const [submission, setSubmission] = useState(null);
    const [polling, setPolling] = useState(false);

    const [status, setStatus] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        fetch(`/api/fis-config/${encodeURIComponent(category)}`, { headers: { ...authHeader() } })
            .then((r) => (r.ok ? r.json() : {}))
            .then((cfg) => {
                const ri = { ...DEFAULT_RACEINFO, ...(cfg?.raceinfo || {}) };
                ri.runs = normalizeRuns(ri.runs);
                const { form: f, notes: n } = toForm(cfg?.raceheader || {}, ri);
                setForm(f);
                setNotes(n);
                setResultType(cfg?.raceheader?.type || "official");
                setRaceinfo(ri);
                setJury(cfg?.jury || []);
            })
            .catch((err) => setError(err.message));

        // Tableau des finales : liste des tours pour la liste de départ.
        fetch(`/api/finals/${encodeURIComponent(category)}`)
            .then((r) => (r.ok ? r.json() : null))
            .then((b) => setBracket(b && b.rounds ? b : null))
            .catch(() => {});
    }, [category]);

    // --- Parcours (Runinfo) ---
    const runs = raceinfo.runs || normalizeRuns();
    const setRun = (index, updater) =>
        setRaceinfo((prev) => {
            const next = normalizeRuns(prev.runs);
            next[index] = updater(next[index]);
            return { ...prev, runs: next };
        });
    const setCourse = (index, field, value) => setRun(index, (r) => ({ ...r, course: { ...r.course, [field]: value } }));
    const setSetter = (index, field, value) =>
        setRun(index, (r) => ({ ...r, course: { ...r.course, coursesetter: { ...r.course.coursesetter, [field]: value } } }));
    const setWeather = (index, field, value) => setRun(index, (r) => ({ ...r, weather: { ...r.weather, [field]: value } }));
    const setForerunner = (index, fi, field, value) =>
        setRun(index, (r) => ({
            ...r,
            course: { ...r.course, forerunners: r.course.forerunners.map((f, i) => (i === fi ? { ...f, [field]: value } : f)) },
        }));
    const addForerunner = (index) =>
        setRun(index, (r) => ({
            ...r,
            course: { ...r.course, forerunners: [...r.course.forerunners, { lastname: "", firstname: "", nation: "" }] },
        }));
    const removeForerunner = (index, fi) =>
        setRun(index, (r) => ({ ...r, course: { ...r.course, forerunners: r.course.forerunners.filter((_, i) => i !== fi) } }));
    const copyRun1ToAll = () =>
        setRaceinfo((prev) => {
            const next = normalizeRuns(prev.runs);
            const src = JSON.stringify(next[0]);
            return { ...prev, runs: [next[0], JSON.parse(src), JSON.parse(src), JSON.parse(src)] };
        });

    const updateRaceinfo = (field, value) => setRaceinfo((prev) => ({ ...prev, [field]: value }));

    const addJuryMember = () => {
        setJury((prev) => [...prev, { function: "TechnicalDelegate", lastname: "", firstname: "", nation: "" }]);
    };
    const updateJuryMember = (index, field, value) => {
        setJury((prev) => prev.map((j, i) => (i === index ? { ...j, [field]: value } : j)));
    };
    const removeJuryMember = (index) => {
        setJury((prev) => prev.filter((_, i) => i !== index));
    };

    // Le champ "Chronométrage" du formulaire commun alimente Timingby.
    const buildFisConfig = () => ({
        raceheader: fromForm(form, resultType),
        raceinfo: { ...raceinfo, timingby: form.timing || raceinfo.timingby },
        jury,
    });

    const { isValid, errors } = validateHeader(form);

    const save = async () => {
        setStatus("");
        setError("");
        try {
            const res = await fetch(`/api/fis-config/${encodeURIComponent(category)}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify(buildFisConfig()),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return false;
            }

            setStatus("Enregistré ✓");
            setNotes([]);
            return true;
        } catch (err) {
            setError(err.message);
            return false;
        }
    };

    const generateXml = async () => {
        setStatus("");
        setError("");
        if (!isValid) {
            setError(`Raceheader incomplet : ${Object.entries(errors).map(([f, m]) => `${f} (${m})`).join(", ")}`);
            return;
        }
        try {
            if (!(await save())) return;

            // 1) Récupère le bracket et dérive le classement (même logique
            // que la vue Classement de Brackets.jsx).
            const bracketRes = await fetch(`/api/finals/${encodeURIComponent(category)}`);
            if (!bracketRes.ok) throw new Error(`Bracket : HTTP ${bracketRes.status}`);
            const bracket = await bracketRes.json();

            if (!bracket || !bracket.rounds) {
                setError("Aucun bracket disponible pour cette catégorie.");
                return;
            }

            const ranking = deriveRanking(bracket);
            if (ranking.length === 0) {
                setError("Le classement est vide — le bracket n'a pas encore de résultats.");
                return;
            }

            // 2) Génère le XML côté serveur. Les non-qualifiés (dans les
            // qualifications mais pas dans le classement des finales) sont
            // ajoutés par le serveur dans <TM_notclassified>.
            const fisConfig = buildFisConfig();
            const xmlRes = await fetch(`/api/finals/export-xml/${encodeURIComponent(category)}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify({ ranking, fisConfig }),
            });

            if (!xmlRes.ok) {
                const data = await xmlRes.json().catch(() => null);
                setError(data?.error || `HTTP ${xmlRes.status}`);
                return;
            }

            const notClassifiedCount = xmlRes.headers.get("X-Not-Classified-Count");
            const blob = await xmlRes.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${fisConfig.raceheader.nation}${fisConfig.raceheader.codex}.xml`;
            a.click();
            window.URL.revokeObjectURL(url);

            setStatus(
                `XML généré et téléchargé ✓ — ${ranking.length} classés` +
                (notClassifiedCount != null ? `, ${notClassifiedCount} non classés` : "")
            );
        } catch (err) {
            setError(err.message);
        }
    };

    // --- Documents FIS (PDF SL/RL, XML liste de départ) ---
    const roundOptions = bracket
        ? [
            ...bracket.rounds.map((round, idx) => ({ value: String(idx), label: ROUND_LABEL[round.length] || `Tour ${idx + 1}` })),
            ...(bracket.smallFinal ? [{ value: "small", label: "Petite finale" }] : []),
        ]
        : [];
    const roundValue = (v) => (v === "small" ? "small" : Number(v));

    const checkHeader = () => {
        setStatus("");
        setError("");
        if (!isValid) {
            setError(`Raceheader incomplet : ${Object.entries(errors).map(([f, m]) => `${f} (${m})`).join(", ")}`);
            return false;
        }
        return true;
    };

    const loadReportData = async () => {
        const res = await fetch(`/api/finals/report-data/${encodeURIComponent(category)}`, { headers: { ...authHeader() } });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        if (!data.bracket || !data.bracket.rounds) throw new Error("Aucun bracket disponible pour cette catégorie.");
        setBracket(data.bracket);
        return data;
    };

    const downloadStartlistPdf = async () => {
        if (!checkHeader()) return;
        try {
            await save();
            await preloadPdfAssets();
            const { bracket: b } = await loadReportData();
            startlistPdf({ fisConfig: buildFisConfig(), bracket: b, roundIndex: roundValue(docRound) });
            setStatus(`${fisFileBase(buildFisConfig().raceheader, "SL")}.pdf téléchargé ✓`);
        } catch (err) {
            setError(err.message);
        }
    };

    const downloadResultPdf = async () => {
        if (!checkHeader()) return;
        try {
            await save();
            await preloadPdfAssets();
            const { bracket: b, qualifiers } = await loadReportData();
            const ranking = deriveRanking(b);
            if (!ranking.length) throw new Error("Le classement est vide — le bracket n'a pas encore de résultats.");
            resultListPdf({ fisConfig: buildFisConfig(), bracket: b, ranking, qualifiers, branding: withLogos ? branding : null });
            setStatus(`${fisFileBase(buildFisConfig().raceheader, "RL")}.pdf téléchargé ✓`);
        } catch (err) {
            setError(err.message);
        }
    };

    const downloadStartlistXml = async () => {
        if (!checkHeader()) return;
        try {
            await save();
            const fisConfig = buildFisConfig();
            const res = await fetch(`/api/finals/export-startlist-xml/${encodeURIComponent(category)}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify({ fisConfig, roundIndex: roundValue(docRound) }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => null);
                throw new Error(data?.error || `HTTP ${res.status}`);
            }
            const url = window.URL.createObjectURL(await res.blob());
            const a = document.createElement("a");
            a.href = url;
            a.download = `${fisFileBase(fisConfig.raceheader, "SL")}.xml`;
            a.click();
            window.URL.revokeObjectURL(url);
            setStatus(`${a.download} téléchargé ✓`);
        } catch (err) {
            setError(err.message);
        }
    };

    // Contenu texte d'un XML généré par finals.js (sans téléchargement).
    const fetchXmlText = async (url, body) => {
        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeader() },
            body: JSON.stringify(body),
        });
        if (!res.ok) {
            const data = await res.json().catch(() => null);
            throw new Error(data?.error || `HTTP ${res.status}`);
        }
        return res.text();
    };

    // Objet par défaut : "FIS/" + nom du PDF (ex: FIS/2026TM8512RL),
    // modifiable dans le champ "Objet".
    const defaultSubject = `FIS/${fisFileBase(fromForm(form, resultType), sendWhat === "results" ? "RL" : "SL")}`;
    const effectiveSubject = subjectEdited ? subject : defaultSubject;

    // Prépare les pièces jointes : PDF RL ou SL (+ XML correspondant).
    const buildFiles = async () => {
        const fisConfig = buildFisConfig();
        const rh = fisConfig.raceheader;
        await preloadPdfAssets();
        const { bracket: b, qualifiers } = await loadReportData();
        const files = [];
        if (sendWhat === "results") {
            const ranking = deriveRanking(b);
            if (!ranking.length) throw new Error("Le classement est vide — le bracket n'a pas encore de résultats.");
            const pdf = resultListPdf({ fisConfig, bracket: b, ranking, qualifiers, branding: withLogos ? branding : null, save: false });
            files.push({ filename: pdf.filename, contentType: "application/pdf", dataUrl: pdf.dataUrl });
            if (includeXml) {
                const text = await fetchXmlText(`/api/finals/export-xml/${encodeURIComponent(category)}`, { ranking, fisConfig });
                files.push({ filename: `${rh.nation}${rh.codex}.xml`, contentType: "application/xml", text });
            }
        } else {
            const roundIndex = roundValue(docRound);
            const pdf = startlistPdf({ fisConfig, bracket: b, roundIndex, save: false });
            files.push({ filename: pdf.filename, contentType: "application/pdf", dataUrl: pdf.dataUrl });
            if (includeXml) {
                const text = await fetchXmlText(`/api/finals/export-startlist-xml/${encodeURIComponent(category)}`, { fisConfig, roundIndex });
                files.push({ filename: `${fisFileBase(rh, "SL")}.xml`, contentType: "application/xml", text });
            }
        }
        return files;
    };

    const sendToFis = async () => {
        if (!checkHeader()) return;
        setSubmission(null);
        try {
            if (!(await save())) return;
            const files = await buildFiles();
            const via = transport === "email" ? `email à ${sendTo}\nObjet : ${effectiveSubject}` : `l'API FIS Member${testMode ? " (MODE TEST)" : ""}`;
            if (
                !window.confirm(
                    `Envoyer à la FIS par ${via}\n\nFichiers :\n${files.map((f) => `• ${f.filename}`).join("\n")}\n\n` +
                    "C'est un envoi officiel : vérifiez d'abord les fichiers téléchargés."
                )
            ) {
                return;
            }

            setSending(true);
            const url = transport === "email" ? "/api/timing/fis-email/send-files" : "/api/timing/fis-api/upload-files";
            const body =
                transport === "email"
                    ? { to: sendTo, subject: effectiveSubject, files }
                    : { files, testMode, description: effectiveSubject };
            const res = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify(body),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || data.message || `HTTP ${res.status}`);

            if (transport === "email") {
                setStatus(`Envoyé à ${sendTo} (« ${effectiveSubject} ») : ${files.map((f) => f.filename).join(", ")} ✓`);
            } else {
                setSubmission(data);
                setStatus(`Envoyé à l'API FIS : ${data.outcome || "en cours de traitement"} ✓`);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setSending(false);
        }
    };

    // Limite FIS : 1 requête de statut / 5 s par utilisateur.
    const pollStatus = async () => {
        if (!submission?.uuid) return;
        setPolling(true);
        try {
            const res = await fetch(`/api/timing/fis-api/status/${encodeURIComponent(submission.uuid)}`, { headers: { ...authHeader() } });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
            setSubmission(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setTimeout(() => setPolling(false), 6000);
        }
    };

    const run = runs[runTab];
    const tf = (label, value, onChange, sm = 4, extra = {}) => (
        <Grid item xs={12} sm={sm}>
            <TextField fullWidth size="small" label={label} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...extra} />
        </Grid>
    );

    return (
        <Box p={3} maxWidth={900}>
            <ParallelSubNav category={category} current="fis" />
            <Typography variant="h4" mb={1}>
                Export FIS — {category}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Fichier XML de résultats FIS (télémark, parallèle). Même saisie que pour les autres courses
                FIS ; cette config est propre à cette catégorie.
            </Typography>

            {notes.map((n, i) => (
                <Alert key={i} severity="warning" sx={{ mb: 2 }}>
                    {n}
                </Alert>
            ))}

            <RaceHeaderForm
                config={form}
                setConfig={setForm}
                onTypeChange={() => {}}
                hideTypeToggle
                hiddenFields={["sector"]}
                disciplineChoices={PARALLEL_DISCIPLINES}
                title="En-tête de course (Raceheader)"
            />

            {/* --- Raceinfo (optionnel) --- */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Informations complémentaires (facultatif)
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Liste FIS utilisée (n°)"
                            value={raceinfo.usedfislist}
                            onChange={(e) => updateRaceinfo("usedfislist", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Pénalité appliquée"
                            value={raceinfo.appliedpenalty}
                            onChange={(e) => updateRaceinfo("appliedpenalty", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Pénalité calculée"
                            value={raceinfo.calculatedpenalty}
                            onChange={(e) => updateRaceinfo("calculatedpenalty", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Traitement des données par"
                            value={raceinfo.dataprocessingby}
                            onChange={(e) => updateRaceinfo("dataprocessingby", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Société du logiciel"
                            value={raceinfo.softwarecompany}
                            onChange={(e) => updateRaceinfo("softwarecompany", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Nom du logiciel"
                            value={raceinfo.softwarename}
                            onChange={(e) => updateRaceinfo("softwarename", e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Version du logiciel"
                            value={raceinfo.softwareversion}
                            onChange={(e) => updateRaceinfo("softwareversion", e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* --- Parcours (Runinfo) --- */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="h6">Parcours et conditions</Typography>
                    <Button size="small" onClick={copyRun1ToAll}>
                        Copier la manche 1 vers 2-4
                    </Button>
                </Box>
                <Tabs value={runTab} onChange={(_, v) => setRunTab(v)} sx={{ mb: 2 }}>
                    {[0, 1, 2, 3].map((i) => (
                        <Tab key={i} label={`Manche ${i + 1}`} />
                    ))}
                </Tabs>

                <Grid container spacing={2}>
                    {tf("Nom de la piste", run.course.name, (v) => setCourse(runTab, "name", v), 8)}
                    {tf("N° d'homologation", run.course.homologation, (v) => setCourse(runTab, "homologation", v), 4)}
                    {tf("Altitude départ (m)", run.course.startelev, (v) => setCourse(runTab, "startelev", v), 3, { type: "number" })}
                    {tf("Altitude arrivée (m)", run.course.finishelev, (v) => setCourse(runTab, "finishelev", v), 3, { type: "number" })}
                    <Grid item xs={12} sm={2}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Dénivelé"
                            value={
                                Number(run.course.startelev) && Number(run.course.finishelev)
                                    ? `${Number(run.course.startelev) - Number(run.course.finishelev)} m`
                                    : ""
                            }
                            InputProps={{ readOnly: true }}
                        />
                    </Grid>
                    {tf("Longueur (m)", run.course.length, (v) => setCourse(runTab, "length", v), 2, { type: "number" })}
                    {tf("Heure de départ", run.starttime, (v) => setRun(runTab, (r) => ({ ...r, starttime: v })), 2, { placeholder: "10:00" })}
                    {tf("Nombre de portes", run.course.gates, (v) => setCourse(runTab, "gates", v), 3, { type: "number" })}
                    {tf("Changements de direction", run.course.turninggates, (v) => setCourse(runTab, "turninggates", v), 3, { type: "number" })}
                </Grid>

                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Traceur
                </Typography>
                <Grid container spacing={2}>
                    {tf("Nom", run.course.coursesetter.lastname, (v) => setSetter(runTab, "lastname", v), 5)}
                    {tf("Prénom", run.course.coursesetter.firstname, (v) => setSetter(runTab, "firstname", v), 5)}
                    {tf("Nation", run.course.coursesetter.nation, (v) => setSetter(runTab, "nation", v.toUpperCase()), 2, { inputProps: { maxLength: 3 } })}
                </Grid>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3, mb: 1 }}>
                    <Typography variant="subtitle2">Ouvreurs</Typography>
                    <Button size="small" startIcon={<AddIcon />} onClick={() => addForerunner(runTab)}>
                        Ajouter
                    </Button>
                </Box>
                {run.course.forerunners.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        Aucun ouvreur.
                    </Typography>
                )}
                {run.course.forerunners.map((f, fi) => (
                    <Grid container spacing={2} key={fi} sx={{ mb: 1 }} alignItems="center">
                        {tf(`Ouvreur ${fi + 1} — nom`, f.lastname, (v) => setForerunner(runTab, fi, "lastname", v), 5)}
                        {tf("Prénom", f.firstname, (v) => setForerunner(runTab, fi, "firstname", v), 4)}
                        {tf("Nation", f.nation, (v) => setForerunner(runTab, fi, "nation", v.toUpperCase()), 2, { inputProps: { maxLength: 3 } })}
                        <Grid item xs={2} sm={1}>
                            <IconButton color="error" onClick={() => removeForerunner(runTab, fi)}>
                                <DeleteIcon />
                            </IconButton>
                        </Grid>
                    </Grid>
                ))}

                <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                    Conditions
                </Typography>
                <Grid container spacing={2}>
                    {tf("Météo", run.weather.weather, (v) => setWeather(runTab, "weather", v), 4, { placeholder: "Ensoleillé" })}
                    {tf("Neige", run.weather.snow, (v) => setWeather(runTab, "snow", v), 4, { placeholder: "Dure" })}
                    {tf("Température air (°C)", run.weather.temperatureair, (v) => setWeather(runTab, "temperatureair", v), 4, { placeholder: "-3.0" })}
                </Grid>
            </Paper>

            {/* --- Jury --- */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Typography variant="h6">Jury</Typography>
                    <Button startIcon={<AddIcon />} onClick={addJuryMember} size="small">
                        Ajouter un membre
                    </Button>
                </Box>

                {jury.length === 0 && (
                    <Typography color="text.secondary" variant="body2">
                        Aucun membre du jury ajouté.
                    </Typography>
                )}

                {jury.map((j, i) => (
                    <Box key={i}>
                        {i > 0 && <Divider sx={{ my: 2 }} />}
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Fonction</InputLabel>
                                    <Select
                                        label="Fonction"
                                        value={j.function}
                                        onChange={(e) => updateJuryMember(i, "function", e.target.value)}
                                    >
                                        {JURY_FUNCTIONS.map((f) => (
                                            <MenuItem key={f} value={f}>
                                                {f}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Nom"
                                    value={j.lastname}
                                    onChange={(e) => updateJuryMember(i, "lastname", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Prénom"
                                    value={j.firstname}
                                    onChange={(e) => updateJuryMember(i, "firstname", e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={10} sm={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Nation"
                                    inputProps={{ maxLength: 3 }}
                                    value={j.nation}
                                    onChange={(e) => updateJuryMember(i, "nation", e.target.value.toUpperCase())}
                                />
                            </Grid>
                            <Grid item xs={2} sm={1}>
                                <IconButton onClick={() => removeJuryMember(i)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </Box>
                ))}
            </Paper>

            {/* --- Documents FIS --- */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Documents FIS
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Fichiers nommés selon la règle FIS : saison + TM + codex + SL (liste de départ) ou RL (résultats),
                    par exemple {fisFileBase(fromForm(form, resultType), "RL") || "2026TM8512RL"}.pdf.
                </Typography>

                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small" disabled={!roundOptions.length}>
                            <InputLabel>Tour (liste de départ)</InputLabel>
                            <Select label="Tour (liste de départ)" value={String(docRound)} onChange={(e) => setDocRound(e.target.value)}>
                                {roundOptions.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={8}>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button variant="outlined" disabled={!roundOptions.length} onClick={downloadStartlistPdf}>
                                PDF liste de départ (SL)
                            </Button>
                            <Button variant="outlined" disabled={!roundOptions.length} onClick={downloadStartlistXml}>
                                XML liste de départ (SL)
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
                {!roundOptions.length && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                        Générez d'abord le bracket depuis le hub des courses parallèles.
                    </Typography>
                )}

                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                    <Button variant="outlined" disabled={!roundOptions.length} onClick={downloadResultPdf}>
                        PDF résultats avec pénalités (RL)
                    </Button>
                    <FormControlLabel
                        control={<Checkbox checked={withLogos && !!branding} disabled={!branding} onChange={(e) => setWithLogos(e.target.checked)} />}
                        label={branding ? "Logos (Config impression) dans le PDF résultats" : "Aucun logo trouvé dans la Config impression"}
                    />
                </Box>
            </Paper>

            {/* --- Envoi à la FIS --- */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Envoi à la FIS
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Contenu</InputLabel>
                            <Select label="Contenu" value={sendWhat} onChange={(e) => setSendWhat(e.target.value)}>
                                <MenuItem value="results">Résultats — PDF RL avec pénalités</MenuItem>
                                <MenuItem value="startlist" disabled={!roundOptions.length}>
                                    Liste de départ — PDF SL du tour sélectionné ci-dessus
                                </MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Transport</InputLabel>
                            <Select label="Transport" value={transport} onChange={(e) => { setTransport(e.target.value); setSubmission(null); }}>
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="api">API FIS Member</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {transport === "email" ? (
                        <>
                            <Grid item xs={12} sm={5}>
                                <TextField fullWidth size="small" label="Destinataire" value={sendTo} onChange={(e) => setSendTo(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Objet"
                                    value={effectiveSubject}
                                    onChange={(e) => { setSubject(e.target.value); setSubjectEdited(true); }}
                                    helperText={subjectEdited ? "Objet modifié à la main" : "Par défaut : FIS/ + nom du fichier"}
                                />
                            </Grid>
                        </>
                    ) : (
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />}
                                label="Mode test (rien n'est publié par la FIS)"
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Checkbox checked={includeXml} onChange={(e) => setIncludeXml(e.target.checked)} />}
                            label={`Joindre aussi le XML (${sendWhat === "results" ? `${form.nation}${form.codex}.xml` : `${fisFileBase(fromForm(form, resultType), "SL")}.xml`})`}
                        />
                    </Grid>
                </Grid>
                <Button variant="contained" color="success" sx={{ mt: 1 }} disabled={sending || !isValid || !roundOptions.length} onClick={sendToFis}>
                    {sending ? "Envoi…" : "Envoyer à la FIS"}
                </Button>

                {submission && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Soumission {submission.uuid} : <b>{submission.outcome}</b>
                            {submission.summary?.isComplete ? " (traitement terminé)" : " (en cours)"}
                        </Typography>
                        {(submission.files || []).length > 0 && typeof submission.files[0] === "object" && (
                            <Table size="small" sx={{ mt: 1 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fichier</TableCell>
                                        <TableCell>Statut</TableCell>
                                        <TableCell>Commentaire</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {submission.files.map((f, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{f.fileName}</TableCell>
                                            <TableCell>{f.status}</TableCell>
                                            <TableCell>{f.comments || ""}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                        {!submission.summary?.isComplete && (
                            <Button size="small" variant="outlined" sx={{ mt: 1 }} disabled={polling} onClick={pollStatus}>
                                {polling ? "Patientez…" : "Vérifier le statut"}
                            </Button>
                        )}
                    </Box>
                )}
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>
                    {error}
                </Alert>
            )}
            {status && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {status}
                </Alert>
            )}

            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                <FormControl size="small" sx={{ minWidth: 280 }}>
                    <InputLabel>Type de résultats</InputLabel>
                    <Select label="Type de résultats" value={resultType} onChange={(e) => setResultType(e.target.value)}>
                        {RESULT_TYPES.map((t) => (
                            <MenuItem key={t.value} value={t.value}>
                                {t.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Button variant="outlined" onClick={save}>
                    Enregistrer
                </Button>
                <Button variant="contained" startIcon={<DownloadIcon />} disabled={!isValid} onClick={generateXml}>
                    Générer et télécharger le XML
                </Button>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: "block" }}>
                Les concurrents des qualifications qui ne figurent pas dans le tableau final sont exportés dans
                &lt;TM_notclassified&gt; (statut DNQ, ou DNS/DNF/DSQ si indiqué dans les qualifications).
            </Typography>
        </Box>
    );
}